import { useCallback, useEffect, useState } from 'react';
import { pdApi } from '../api';
import '../screens.css';
import './Financial.css';
import { HelpPanel } from '../HelpPanel';
import { MVP_COST_SCENARIOS, NPV_COST_SCENARIOS } from '../helpContent';
import { SelectField, PROMPT } from '@platform/ui-kit';

/**
 * Financial Modeling → MVP Cost & Scenarios, and → NPV Cost & Scenarios.
 *
 * Cloned from `views/value-roi/cost-scenarios.ejs` and `views/npv-value-model/npv-costs.ejs`.
 * One component, two contexts — the source's two files differ only in which family of
 * scenarios they materialise and which default code they fall back to.
 *
 * OPENING THIS SCREEN CREATES MODEL VERSIONS. `getOrCreateAllScenarios` materialises a
 * version row for every active scenario type the first time an opportunity is opened, so
 * a GET writes. That is the source's design and the tabs below depend on it: each tab is
 * a version, and a version has to exist before a cost can hang off it.
 *
 * THE ONE-TIME / RECURRING SPLIT DEPENDS ONLY ON `frequency` — never on the cost type's
 * name. The source is explicit about this, and it is what makes the build cost a simple
 * sum of the one-time lines.
 *
 * Entering 0 for a one-time line DELETES it rather than storing a zero, so the cost list
 * only ever holds real numbers.
 */

type Dict = Record<string, unknown>;

const money = (v: unknown) =>
  v === null || v === undefined
    ? '$0.00'
    : Number(v).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

export function CostScenarios({ context }: { context: 'MVP' | 'NPV' }) {
  const [data, setData] = useState<Dict | null>(null);
  const [projectId, setProjectId] = useState('');
  const [opportunityId, setOpportunityId] = useState('');
  const [scenario, setScenario] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState<Dict | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = (await pdApi.financial.costScenarios({
        context,
        project: projectId || undefined,
        opportunity: opportunityId || undefined,
        scenario: scenario || undefined,
      })) as Dict;
      setData(d);
      if (!scenario && d.selectedScenarioType) setScenario(String(d.selectedScenarioType));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [context, projectId, opportunityId, scenario]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (ok) setNotice(ok);
      setAdding(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const projects = (data?.projects as Dict[]) ?? [];
  const opportunities = (data?.opportunities as Dict[]) ?? [];
  const scenarioTypes = (data?.scenarioTypes as Dict[]) ?? [];
  const costTypes = (data?.costTypes as Dict[]) ?? [];
  const costs = (data?.costs as Dict[]) ?? [];
  const model = (data?.financialModel as Dict) ?? {};
  const versionId = data?.selectedVersionId as number | undefined;

  const saveRunRates = () =>
    run(
      () =>
        pdApi.financial.saveModel(opportunityId, {
          financial_model_version_id: versionId,
          build_cost: model.build_cost,
          current_run_rate: model.current_run_rate,
          future_run_rate: model.future_run_rate,
          annual_hosting_cost: model.annual_hosting_cost,
          annual_support_cost: model.annual_support_cost,
        }),
      'Financial model saved',
    );

  const setModel = (k: string, v: string) =>
    setData({ ...(data ?? {}), financialModel: { ...model, [k]: v } });

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">
            {context === 'NPV' ? 'NPV Cost & Scenarios' : 'MVP Cost & Scenarios'}
          </h1>
          <p className="pd-page-header__subtitle">
            Enter what this opportunity costs to build and to run. Each tab is a scenario —
            the same opportunity modelled under different assumptions.
          </p>
        </div>
        {/* One screen, two value models — the panel follows the `context` prop, as the
            source ships a separate help object per view. */}
        <div className="pd-page-header__actions">
          <HelpPanel help={context === 'NPV' ? NPV_COST_SCENARIOS : MVP_COST_SCENARIOS} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}
      {notice ? <div className="pd-notice">{notice}</div> : null}

      {/*
        THE SOURCE'S OWN SELECTOR ROW — labelled, and sized so an id plus its description
        fits (`views/value-roi/cost-scenarios.ejs:154-190`). It used to render as two bare
        `.pd-filters` selects: this module's generic filter bar, which is 13px, square and
        unlabelled. The placeholder wording is the source's too, including the way the
        opportunity list says WHY it is empty rather than just being empty.
      */}
      <div className="fin-selectors">
        <div className="fin-selector fin-selector--project">
          <SelectField
            id="fin-project"
            label="Project"
            width="lg"
            placeholder={PROMPT.project}
            value={projectId}
            onChange={(v) => {
              setProjectId(v);
              setOpportunityId('');
            }}
            options={projects.map((p) => ({
              value: String(p.project_id),
              id: String(p.project_id),
              description: String(p.project_description),
            }))}
          />
        </div>

        <div className="fin-selector fin-selector--opportunity">
          <SelectField
            id="fin-opportunity"
            label="Opportunity"
            width="lg"
            value={opportunityId}
            disabled={!projectId}
            onChange={setOpportunityId}
            placeholder={
              !projectId
                ? '— Select a Project first —'
                : opportunities.length === 0
                  ? '— No Opportunities in this Project —'
                  : PROMPT.opportunity
            }
            options={opportunities.map((o) => ({
              value: String(o.opportunity_id),
              id: String(o.opportunity_id),
              description: String(o.description),
            }))}
          />
        </div>
      </div>

      {!opportunityId ? (
        <div className="pd-empty">Choose a project and an opportunity to model its costs.</div>
      ) : loading ? (
        <div className="pd-loading">Loading…</div>
      ) : (
        <>
          {scenarioTypes.length === 0 ? (
            <div className="pd-notice">
              No active {context} scenario is configured. Activate one in Scenario Setup
              before entering costs.
            </div>
          ) : (
            <div className="pd-tabs">
              {scenarioTypes.map((s) => (
                <button
                  key={String(s.code)}
                  type="button"
                  className={`pd-tab${scenario === s.code ? ' pd-tab--active' : ''}`}
                  onClick={() => setScenario(String(s.code))}
                >
                  {String(s.name)}
                </button>
              ))}
            </div>
          )}

          <div className="fin-grid">
            <section className="pd-form-section">
              <h2 className="pd-section-title">Run rates</h2>
              <p className="pd-hint">
                Annual savings is current run rate minus future run rate. It is the
                numerator of every ROI on the following screens.
              </p>
              {[
                ['current_run_rate', 'Current Run Rate (annual)'],
                ['future_run_rate', 'Future Run Rate (annual)'],
                ['annual_hosting_cost', 'Annual Hosting Cost'],
                ['annual_support_cost', 'Annual Support Cost'],
              ].map(([k, label]) => (
                <div className="pd-field" key={k}>
                  <label htmlFor={`fm-${k}`}>{label}</label>
                  <input
                    id={`fm-${k}`}
                    type="number"
                    value={String(model[k!] ?? '')}
                    onChange={(e) => setModel(k!, e.target.value)}
                  />
                </div>
              ))}
              <div className="fin-derived">
                Annual savings:{' '}
                <strong>
                  {money(
                    (Number(model.current_run_rate) || 0) - (Number(model.future_run_rate) || 0),
                  )}
                </strong>
              </div>
              <button
                type="button"
                className="pd-btn pd-btn--primary"
                disabled={busy}
                onClick={saveRunRates}
              >
                Save run rates
              </button>
            </section>

            <section className="pd-form-section">
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <h2 className="pd-section-title" style={{ margin: 0 }}>
                  Cost lines
                </h2>
                <button
                  type="button"
                  className="pd-btn"
                  disabled={!versionId}
                  onClick={() =>
                    setAdding({
                      cost_type_id: costTypes[0]?.id,
                      frequency: 'one-time',
                      amount: '',
                      label: '',
                    })
                  }
                >
                  Add cost
                </button>
              </div>

              <div className="pd-table-wrap">
                <table className="pd-table">
                  <thead>
                    <tr>
                      <th style={{ width: 190 }}>Cost Type</th>
                      <th>Label</th>
                      <th style={{ width: 110 }}>Frequency</th>
                      <th style={{ width: 120 }}>Amount</th>
                      <th style={{ width: 60 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {costs.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="pd-empty">
                          No costs entered for this scenario.
                        </td>
                      </tr>
                    ) : (
                      costs.map((c) => (
                        <tr key={String(c.id)}>
                          <td>{String(c.cost_type_name)}</td>
                          <td>{String(c.label ?? '')}</td>
                          <td>{String(c.frequency)}</td>
                          <td className="pd-num">{money(c.amount)}</td>
                          <td>
                            <button
                              type="button"
                              className="pd-icon-btn"
                              title="Delete"
                              onClick={() =>
                                run(() =>
                                  pdApi.financial.deleteCost(opportunityId, Number(c.id)),
                                )
                              }
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="fin-totals">
                <div>
                  <span>One-time</span>
                  <strong>{money(data?.totalOneTime)}</strong>
                </div>
                <div>
                  <span>Monthly</span>
                  <strong>{money(data?.totalMonthly)}</strong>
                </div>
                <div>
                  <span>Annual</span>
                  <strong>{money(data?.totalAnnual)}</strong>
                </div>
                <div className="fin-totals-grand">
                  <span>Total (Year 1)</span>
                  <strong>{money(data?.grandTotal)}</strong>
                </div>
              </div>
              <p className="pd-hint">
                Build cost is the sum of the one-time lines — that is what ROI and payback
                divide by.
              </p>
            </section>
          </div>
        </>
      )}

      {adding ? (
        <div className="pd-modal-backdrop" onClick={() => setAdding(null)}>
          <div className="pd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal__head">
              <h2 className="pd-modal__title">Add cost line</h2>
              <button type="button" className="pd-icon-btn" onClick={() => setAdding(null)}>
                ✕
              </button>
            </div>
            <div className="pd-modal__body">
              <div className="pd-field">
                <label htmlFor="ac-type">Cost type *</label>
                <select
                  id="ac-type"
                  value={String(adding.cost_type_id ?? '')}
                  onChange={(e) => {
                    const ct = costTypes.find((c) => String(c.id) === e.target.value);
                    setAdding({
                      ...adding,
                      cost_type_id: e.target.value,
                      // Default the frequency from the cost type, as the source does.
                      frequency: ct?.default_frequency ?? adding.frequency,
                    });
                  }}
                >
                  {costTypes.map((c) => (
                    <option key={String(c.id)} value={String(c.id)}>
                      {String(c.name)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pd-field">
                <label htmlFor="ac-label">Label</label>
                <input
                  id="ac-label"
                  type="text"
                  value={String(adding.label ?? '')}
                  onChange={(e) => setAdding({ ...adding, label: e.target.value })}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="pd-field">
                  <label htmlFor="ac-freq">Frequency *</label>
                  <select
                    id="ac-freq"
                    value={String(adding.frequency ?? 'one-time')}
                    onChange={(e) => setAdding({ ...adding, frequency: e.target.value })}
                  >
                    <option value="one-time">one-time</option>
                    <option value="monthly">monthly</option>
                    <option value="annual">annual</option>
                  </select>
                </div>
                <div className="pd-field">
                  <label htmlFor="ac-amount">Amount *</label>
                  <input
                    id="ac-amount"
                    type="number"
                    value={String(adding.amount ?? '')}
                    onChange={(e) => setAdding({ ...adding, amount: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="pd-modal__foot">
              <button type="button" className="pd-btn" onClick={() => setAdding(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="pd-btn pd-btn--primary"
                disabled={busy}
                onClick={() =>
                  run(
                    () =>
                      pdApi.financial.addCost(opportunityId, {
                        ...adding,
                        financial_model_version_id: versionId,
                      }),
                    'Cost added',
                  )
                }
              >
                Add
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
